/**
 * ============================================================================
 * NOT FOUND PAGE - 404 Error
 * ============================================================================
 * 
 * Custom 404 page for better UX
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Card from '../common/Card';
import { PublicHeader, PublicFooter } from '../public';
import '../../styles/animations.css';
import '../../styles/global.css';

/**
 * NotFound Page Component
 * @returns {JSX.Element}
 */
const NotFound = () => {
  const navigate = useNavigate();
  const { language } = useI18n();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card
          variant="glass"
          className="backdrop-blur-xl border-white/20 dark:border-white/10 p-12 text-center animate-fadeInUp"
          hover
        >
          <div className="w-32 h-32 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-6xl shadow-lg shadow-red-500/30 animate-float">
            404
          </div>
          <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-red-600 via-orange-600 to-pink-600 bg-clip-text text-transparent mb-4">
            {language === 'fr' ? 'Page Non Trouvée' : 'Page Not Found'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Désolé, la page que vous recherchez n\'existe pas ou a été déplacée.'
              : 'Sorry, the page you\'re looking for doesn\'t exist or has been moved.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="gradient"
              size="lg"
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
            >
              🏠 {language === 'fr' ? 'Retour à l\'Accueil' : 'Back to Home'}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate(-1)}
            >
              ← {language === 'fr' ? 'Page Précédente' : 'Go Back'}
            </Button>
          </div>
        </Card>

        {/* Helpful Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 p-6 text-center hover:scale-105 transition-all cursor-pointer animate-fadeInUp"
            style={{ animationDelay: '0.1s' }}
            hover
            clickable
            onClick={() => navigate('/help')}
          >
            <div className="text-3xl mb-3">❓</div>
            <h3 className="font-black text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Centre d\'Aide' : 'Help Center'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'Trouvez des réponses' : 'Find answers'}
            </p>
          </Card>

          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 p-6 text-center hover:scale-105 transition-all cursor-pointer animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}
            hover
            clickable
            onClick={() => navigate('/contact')}
          >
            <div className="text-3xl mb-3">💬</div>
            <h3 className="font-black text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'Obtenez de l\'aide' : 'Get help'}
            </p>
          </Card>

          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 p-6 text-center hover:scale-105 transition-all cursor-pointer animate-fadeInUp"
            style={{ animationDelay: '0.3s' }}
            hover
            clickable
            onClick={() => navigate('/how-it-works')}
          >
            <div className="text-3xl mb-3">📖</div>
            <h3 className="font-black text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Comment Ça Marche' : 'How It Works'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'En savoir plus' : 'Learn more'}
            </p>
          </Card>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default NotFound;
